/**
 * Genera entries de proyección de impuestos (IVA neto + PPM) por período tributario,
 * para mostrar en el panel futuro de FlujoCajaV2 y el dashboard de IVA.
 *
 * Reglas:
 * - IVA débito: suma monto_iva de emitidas. Notas de crédito (DTE 61) restan.
 * - IVA crédito: suma monto_iva de recibidas. Notas de crédito (DTE 61) restan.
 * - IVA neto = débito - crédito (solo se proyecta si > 0).
 * - PPM 2% se calcula sobre ventas afectas (DTE 33 + 56). Excluye exenta (34),
 *   nota de crédito (61), exportación (110). Régimen Pro Pyme tiene tasas
 *   distintas — eso se valida con contador y se pasa por parámetro a futuro.
 * - Fecha de pago: día 12 del mes siguiente al período.
 * - Solo proyecta períodos cuyo fechaPago > hoy. Períodos pasados se asumen pagados
 *   (la detección de Pagado/Vencido se hace afuera contra movimientos_caja).
 *
 * Adicionalmente, proyecta hasta 3 meses futuros sin datos SII basándose en el
 * promedio de los últimos 3 períodos con datos (estimación). Esos entries
 * llevan `estimado: true`.
 *
 * @param {Array} emitidas - facturas_sii_emitidas con monto_iva, monto_neto, periodo_tributario, tipo_dte
 * @param {Array} recibidas - facturas_sii_recibidas con monto_iva, periodo_tributario, tipo_dte
 * @param {string} pais - "Chile" | "Peru" (afecta solo el label IVA/IGV)
 * @returns {Array} entries con shape { id, fecha_display, importeEnMoneda, razon_social_receptor, tipo, estimado? }
 */
export const calcularImpuestos = (emitidas, recibidas, pais) => {
  const hoy = new Date().toISOString().split("T")[0];
  const entries = [];
  const tasaPPM = 0.02;
  const TIPOS_AFECTOS_PPM = [33, 56];

  const ivaVentasPorMes = {};
  const ivaComprasPorMes = {};
  const ventasNetasPorMes = {};

  for (const f of emitidas) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    const signoEmitida = f.tipo_dte === 61 ? -1 : 1;
    ivaVentasPorMes[mes] = (ivaVentasPorMes[mes] || 0) + signoEmitida * Number(f.monto_iva || 0);
    ventasNetasPorMes[mes] =
      (ventasNetasPorMes[mes] || 0) + (TIPOS_AFECTOS_PPM.includes(f.tipo_dte) ? Number(f.monto_neto || 0) : 0);
  }

  for (const f of recibidas) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    const signoRecibida = f.tipo_dte === 61 ? -1 : 1;
    ivaComprasPorMes[mes] = (ivaComprasPorMes[mes] || 0) + signoRecibida * Number(f.monto_iva || 0);
  }

  const periodos = [...new Set([...Object.keys(ivaVentasPorMes), ...Object.keys(ivaComprasPorMes)])].sort();

  for (const periodo of periodos) {
    const [yyyy, mm] = periodo.split("-");
    const mesSiguiente = parseInt(mm, 10) === 12 ? 1 : parseInt(mm, 10) + 1;
    const añoSiguiente = parseInt(mm, 10) === 12 ? parseInt(yyyy, 10) + 1 : parseInt(yyyy, 10);
    const fechaPago = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;

    if (fechaPago <= hoy) continue;

    const ivaVentas = ivaVentasPorMes[periodo] || 0;
    const ivaCompras = ivaComprasPorMes[periodo] || 0;
    const ivaNeto = ivaVentas - ivaCompras;
    const ventasNetas = ventasNetasPorMes[periodo] || 0;
    const ppm = Math.round(ventasNetas * tasaPPM);

    const labelIVA = pais === "Chile" ? "IVA" : "IGV";

    if (ivaNeto > 0) {
      entries.push({
        id: `iva-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -ivaNeto,
        razon_social_receptor: `${labelIVA} ${periodo}`,
        tipo: "impuesto",
      });
    }

    if (ppm > 0) {
      entries.push({
        id: `ppm-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -ppm,
        razon_social_receptor: `PPM 2% ${periodo}`,
        tipo: "impuesto",
      });
    }
  }

  const labelIVAEst = pais === "Chile" ? "IVA" : "IGV";
  const periodosConDatos = periodos.filter((p) => ivaVentasPorMes[p] > 0);
  const ultimos3 = periodosConDatos.slice(-3);

  if (ultimos3.length > 0) {
    const promedioIVA = Math.round(
      ultimos3.reduce((sum, p) => {
        const ivaNeto = (ivaVentasPorMes[p] || 0) - (ivaComprasPorMes[p] || 0);
        return sum + Math.max(ivaNeto, 0);
      }, 0) / ultimos3.length
    );

    const promedioPPM = Math.round(
      ultimos3.reduce((sum, p) => sum + Math.round((ventasNetasPorMes[p] || 0) * tasaPPM), 0) / ultimos3.length
    );

    const hoyDate = new Date();
    for (let i = 0; i <= 3; i++) {
      const mesProyectado = new Date(hoyDate.getFullYear(), hoyDate.getMonth() + i, 1);
      const yyyy = mesProyectado.getFullYear();
      const mm = String(mesProyectado.getMonth() + 1).padStart(2, "0");
      const periodoProyectado = `${yyyy}-${mm}`;

      if (ivaVentasPorMes[periodoProyectado] && ivaVentasPorMes[periodoProyectado] > 0) continue;

      const mesPago = mesProyectado.getMonth() + 2 > 12 ? 1 : mesProyectado.getMonth() + 2;
      const añoPago = mesProyectado.getMonth() + 2 > 12 ? yyyy + 1 : yyyy;
      const fechaPago = `${añoPago}-${String(mesPago).padStart(2, "0")}-12`;

      if (promedioIVA > 0) {
        entries.push({
          id: `iva-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioIVA,
          razon_social_receptor: `${labelIVAEst} ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }

      if (promedioPPM > 0) {
        entries.push({
          id: `ppm-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioPPM,
          razon_social_receptor: `PPM 2% ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }
    }
  }

  return entries;
};
